import { FormControl, InputLabel, ListSubheader, MenuItem, Select, FormHelperText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CustomAutocomplete from 'components/Input/CustomAutocomplete';
import { SET_FORMATTED_HOSPITAL_DATA } from 'store/key';
import usePrevious from 'hooks/usePrevious';
import { addRemoveRequest, deleteRemoveRequest } from 'utility/general';

const useStyles = makeStyles((theme) => ({
    listSubheader: {
        background: '#fff',
    },
    menuItem: {
        paddingLeft: '26px',
    },
    root: {
        background: '#f7f7f7',
    },
    select: {
        fontSize: 14,
    },
    inputLabel: {
        fontSize: 14,
    },
}));
/**
 * @description: Match Hospital Or Institutions select / Autocomplete
 * @param {Object} props
 * @param {Boolean} props.isAuthorizedEmail Autocomplete freeSolo
 * @param {String} props.hospitalOrInstitution TextField value
 * @param {Function} props.setHospitalOrInstitution Set textField value
 * @param {Array} props.hospitalList Select options
 * @param {String} props.label Placeholder
 * @param {Boolean} props.showPlaceholder
 * @param {String} props.name TextField unique key
 * @param {Boolean} props.filterByGroup Filter select options
 * @param {Boolean} props.innerSearch Autocomplete innerSearch
 * @return {Element}
 */
function HospitalOrInstitutions(props) {
    const classes = useStyles();
    const {
        isAuthorizedEmail = true,
        hospitalOrInstitution,
        setHospitalOrInstitution,
        deleteRequestKey,
        label,
        showPlaceholder = true,
        name,
        allowAction = true,
        filterByGroup = true,
        innerSearch = true,
        helperText,
        error,
        isValidValue = () => null,
        isRequired,
        forceErrorDisplay,
        minLength,
        maxLength,
        allowClusters = [],
        disabled,
        updateApplicationFormOnDisableChange = -1,
        updateSiteFormOnDisableChange = -1,
        disableUniversityChoice = false,
        disableHKU = false,
        disableCUHK = false,
        onAppFieldValidate,
        disableOnPermission = false,
        showOtherOption = false,
        test,
        ...other
    } = props;
    const labelText = showPlaceholder ? (label ? label : 'Hospital / Institution') : null;
    const hospitals = useSelector((state) => state.system.hospitals);
    const [clusterChoice, setClusterChoice] = useState(allowClusters); // allow cluster changed should update the list
    const dispatch = useDispatch();
    const previousDisable = usePrevious(`${allowAction}`);

    const checkAppFieldValidate = () => {
        if (onAppFieldValidate !== undefined) {
            if (!name) {
                throw new Error('name is required');
            }

            const isDisabled = getDisableExceptionPermission();

            if (isDisabled) {
                onAppFieldValidate(name, true);
            } else {
                if (hospitalOrInstitution) {
                    onAppFieldValidate(name, true);
                } else {
                    onAppFieldValidate(name, false);
                }
            }
        }
    };

    useEffect(() => {
        checkAppFieldValidate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hospitalOrInstitution]);

    useEffect(() => {
        // updateApplicationFormOnDisableChange = application id

        if (previousDisable && (updateApplicationFormOnDisableChange >= 0 || updateSiteFormOnDisableChange >= 0)) {
            if (!name) {
                throw new Error('name is required');
            }

            const isDisabled = getDisableExceptionPermission();
            if (isDisabled) {
                addRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    name: deleteRequestKey ?? name,
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            } else {
                deleteRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    keys: [deleteRequestKey ?? name],
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            }
        }

        checkAppFieldValidate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowAction, disabled]);

    const getDisableExceptionPermission = () => {
        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const getDisableStatus = () => {
        if (disableOnPermission) {
            return true;
        }

        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const getFormattedHospitalData = (data = []) => {
        let list = [];
        for (let i of data) {
            if (clusterChoice.length > 0) {
                const isAllowed = clusterChoice.includes(i.clusterName);

                if (!isAllowed) {
                    continue;
                }
            }

            for (let n of i.hospital) {
                // filter option "OTHER" if it is not enabled
                if (!showOtherOption && n.hospitalCode === 'OTHERS') {
                    continue;
                }

                if (disableUniversityChoice && n.hospitalCode === 'HKU') {
                    continue;
                }

                if (disableUniversityChoice && n.hospitalCode === 'CUHK') {
                    continue;
                }

                if (disableCUHK && n.hospitalCode === 'CUHK') {
                    continue;
                }

                if (disableHKU && n.hospitalCode === 'HKU') {
                    continue;
                }

                list.push({ ...n, clusterCode: i.clusterCode, clusterName: i.clusterName });
            }
        }
        return list;
    };

    // initial the data if hospital already exist in redux
    const [hospitalOrInstitutionList, setHospitalOrInstitutionList] = useState(getFormattedHospitalData(hospitals));

    useEffect(() => {
        const list = getFormattedHospitalData(hospitals);
        setHospitalOrInstitutionList(list);
        // eslint-disable-next-line
    }, [hospitals, clusterChoice, disableHKU, disableCUHK, disableUniversityChoice]);

    useEffect(() => {
        dispatch({
            type: SET_FORMATTED_HOSPITAL_DATA,
            payload: {
                formattedHospitalData: hospitalOrInstitutionList,
            },
        });
        // eslint-disable-next-line
    }, [hospitalOrInstitutionList]);

    useEffect(() => {
        if (JSON.stringify(allowClusters) !== JSON.stringify(clusterChoice)) {
            setClusterChoice(allowClusters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowClusters]);

    const getHospitalList = () => {
        if (hospitals.length === 0) {
            return null;
        }

        return hospitals
            .filter((item) => {
                if (allowClusters.length > 0) {
                    return allowClusters.some((item) => item === item.clusterName);
                }

                return true;
            })
            .map((i) => {
                let html = [
                    <ListSubheader key={i.clusterCode} className={classes.listSubheader}>
                        {i.clusterName}
                    </ListSubheader>,
                ];
                i.groups.map((n) =>
                    html.push(
                        <MenuItem key={n.hospitalCode} value={n.hospitalCode} className={classes.menuItem}>
                            {n.hospitalName}
                        </MenuItem>,
                    ),
                );
                return html;
            });
    };

    if (innerSearch) {
        return (
            <CustomAutocomplete
                name={name ? name : 'hospitalOrInstitution'}
                filterByGroup={filterByGroup}
                labelText={labelText}
                value={hospitalOrInstitution}
                options={hospitalOrInstitutionList}
                valueOnChange={setHospitalOrInstitution}
                freeSolo={!isAuthorizedEmail}
                error={error}
                helperText={helperText}
                isValidValue={isValidValue}
                isRequired={isRequired}
                forceErrorDisplay={forceErrorDisplay}
                minLength={minLength}
                maxLength={maxLength}
                disabled={getDisableStatus()}
                {...other}
            />
        );
    }

    const extraProps = {};

    if (showPlaceholder) {
        extraProps.label = labelText;
    }

    return (
        <FormControl
            error={error}
            fullWidth
            variant="outlined"
            size="small"
            className={getDisableStatus() ? 'disabled' : ''}
        >
            {showPlaceholder && <InputLabel className={classes.inputLabel}>{labelText}</InputLabel>}
            <Select
                classes={{ select: classes.select }}
                required
                id={label + '_hospitalOrInstitution'}
                name={name ? name : 'hospitalOrInstitution'}
                label={labelText}
                value={hospitalOrInstitution}
                onChange={setHospitalOrInstitution}
                disabled={getDisableStatus()}
                {...extraProps}
                {...other}
            >
                {getHospitalList()}
            </Select>
            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    );
}

export default HospitalOrInstitutions;
